import React from 'react';
import { Box } from '@mui/material';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

/**
 * The panel MUST declare its own width, and the reason is the §13 page frame.
 *
 * OWNER SCREENSHOT 2026-08-27 17:23 — "新建的组件，翻页到第二页，无法显示". Page 2 of the
 * component library held exactly ONE card (the tenant's 16th component, alone after 15
 * seeded ones at the xl page size of 15), and it rendered as a **19px-wide sliver** with
 * its avatar clipped at the border. Measured off the screenshot: white fill, 1px
 * `#E0E0E0` border — a `ComponentCard`, squeezed, not a missing card.
 *
 * Every admin page opens with the §13 frame:
 *
 *     display: flex; flex-direction: column; alignItems: 'flex-start'
 *
 * In a COLUMN flex container, `align-items: flex-start` makes each child's horizontal
 * size shrink-to-fit instead of stretch. So a direct child that does not declare a width
 * is sized by its content — and the grid inside is full of `width: '100%'` cards and
 * `minWidth: 0` boxes, which contribute ~nothing to intrinsic width. With fifteen cards
 * the wrapping grid's max-content is wide enough to hide this; with ONE card the whole
 * chain collapses, and the card's own `width: '100%'` then resolves against the collapsed
 * box.
 *
 * That is why the defect looked like a pagination bug and is not one: the arithmetic in
 * `paginatedComponents` was always right, and a jsdom test finds the card on page 2 —
 * jsdom does no layout, so it cannot see a collapse. The evidence had to come from the
 * pixels.
 *
 * `minWidth: 0` as well as `width: '100%'`: without it a flex item's `min-width: auto`
 * floors it at min-content, which is how a wide table inside a panel pushes the whole
 * page sideways instead of scrolling.
 */
const TabPanel = ({ children, value, index, ...other }: TabPanelProps) => (
  <Box
    role="tabpanel"
    hidden={value !== index}
    id={`form-components-tabpanel-${index}`}
    aria-labelledby={`form-components-tab-${index}`}
    sx={{ width: '100%', minWidth: 0 }}
    {...other}
  >
    {value === index && <Box sx={{ width: '100%', minWidth: 0 }}>{children}</Box>}
  </Box>
);

export default TabPanel;
